import React, { useState, useEffect } from 'react'
import { Save, RotateCcw, HelpCircle } from 'lucide-react'
import { Breadcrumb } from '../../components/Common'
import HelpModal from '../../components/HelpModal/HelpModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import approverMasterService from '../../services/approverMasterService'

const ApproverMasterForm = () => {
  const navigate = useNavigate()
  const [formData, setFormData] = useState({
    approverCode: '',
    approverName: '',
    employee: '',
    user: '',
    email: '',
    phone: '',
    department: '',
    designation: '',
    status: 'Active',
    approvalLimits: {
      dailyLimit: 0,
      monthlyLimit: 0,
      perTransactionLimit: 0,
      currency: 'INR'
    },
    approvalLevel: 1,
    approvalScope: 'All',
    allowedDepartments: [],
    allowedCategories: [],
    allowedEmployees: [],
    notifyOnSubmission: true,
    notifyOnApproval: false,
    notifyOnRejection: true,
    notes: '',
    sortOrder: 0
  })
  const [isLoadingApproverCode, setIsLoadingApproverCode] = useState(true)
  const [showHelp, setShowHelp] = useState(false)

  // Fetch next approver code
  useEffect(() => {
    const fetchApproverCode = async () => {
      try {
        setIsLoadingApproverCode(true)
        const response = await approverMasterService.getNextApproverCode()
        if (response.success && response.data) {
          setFormData(prev => ({
            ...prev,
            approverCode: response.data.approverCode || response.data.formattedValue || 'APPR0001'
          }))
        }
      } catch (error) {
        console.error('Error fetching approver code:', error)
        setFormData(prev => ({ ...prev, approverCode: 'APPR0001' }))
      } finally {
        setIsLoadingApproverCode(false)
      }
    }
    fetchApproverCode()
  }, [])

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    if (name.startsWith('approvalLimits.')) {
      const limitField = name.split('.')[1]
      setFormData(prev => ({
        ...prev,
        approvalLimits: {
          ...prev.approvalLimits,
          [limitField]: limitField === 'currency' ? value : (parseFloat(value) || 0)
        }
      }))
    } else if (name === 'allowedDepartments') {
      // Handle multi-select for departments (comma-separated)
      const departments = value.split(',').map(d => d.trim()).filter(d => d)
      setFormData(prev => ({
        ...prev,
        allowedDepartments: departments
      }))
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : (type === 'number' ? parseFloat(value) || 0 : value)
      }))
    }
  }

  const handleSave = async () => {
    try {
      // Validation
      if (!formData.approverName || !formData.email) {
        toast.error('Please fill in all required fields')
        return
      }

      const response = await approverMasterService.createApprover(formData)
      if (response.success) {
        toast.success('Approver created successfully!')
        navigate('/expenses/approver-master')
      } else {
        toast.error(response.message || 'Failed to create approver')
      }
    } catch (error) {
      console.error('Error saving approver:', error)
      if (error.response?.status === 400) {
        toast.error(error.response.data?.message || 'Invalid data provided')
      } else {
        toast.error('Failed to create approver')
      }
    }
  }

  const handleReset = () => {
    setFormData({
      approverCode: formData.approverCode, // Keep the code
      approverName: '',
      employee: '',
      user: '',
      email: '',
      phone: '',
      department: '',
      designation: '',
      status: 'Active',
      approvalLimits: {
        dailyLimit: 0,
        monthlyLimit: 0,
        perTransactionLimit: 0,
        currency: 'INR'
      },
      approvalLevel: 1,
      approvalScope: 'All',
      allowedDepartments: [],
      allowedCategories: [],
      allowedEmployees: [],
      notifyOnSubmission: true,
      notifyOnApproval: false,
      notifyOnRejection: true,
      notes: '',
      sortOrder: 0
    })
  }

  const breadcrumbItems = [
    { label: 'Expenses', path: '/expenses', onClick: () => navigate('/expenses') },
    { label: 'Approver Master', path: '/expenses/approver-master', onClick: () => navigate('/expenses/approver-master') },
    { label: 'New Approver', path: '/expenses/approver-master/form', isCurrent: true }
  ]

  const helpContent = (
    <>
      <h3>What is Approver Master?</h3>
      <p>This screen lets you <span className="help-term">create a new approver</span>—a person who can approve expense claims or requests within the system. Approvers have approval levels, financial limits, and optional scope (e.g. by department or category).</p>

      <h3>Navigation</h3>
      <p>Use the <span className="help-term">back arrow</span> in the breadcrumb to go back, or click <span className="help-term">Expenses</span> or <span className="help-term">Approver Master</span> to jump to those sections.</p>

      <h3>Approver Information</h3>
      <ul>
        <li><strong>Approver Code *</strong> – Auto-generated unique code (e.g. APR/0002). Read-only.</li>
        <li><strong>Approver Name *</strong> – Full name of the approver (required).</li>
        <li><strong>Status *</strong> – <em>Active</em> (can approve) or <em>Inactive</em> (disabled).</li>
        <li><strong>Approval Level</strong> – Numeric level (e.g. 1, 2) for approval hierarchy; higher levels may approve after lower.</li>
        <li><strong>Email *</strong> – Contact email (required).</li>
        <li><strong>Phone</strong> – Optional phone number.</li>
        <li><strong>Department</strong> – Department the approver belongs to.</li>
        <li><strong>Designation</strong> – Job title or role.</li>
      </ul>

      <h3>Approval Limits</h3>
      <p>Financial limits for what this approver can approve (use 0 for no limit):</p>
      <ul>
        <li><strong>Daily Limit</strong> – Max total amount approvable per day.</li>
        <li><strong>Monthly Limit</strong> – Max total amount approvable per month.</li>
        <li><strong>Per Transaction Limit</strong> – Max amount per single approval.</li>
        <li><strong>Currency</strong> – Currency for these limits (INR, USD, EUR, GBP).</li>
        <li><strong>Approval Scope</strong> – <em>All</em>, or restricted by Department, Category, Employee, or Custom. If Department, you can set allowed departments.</li>
        <li><strong>Sort Order</strong> – Number to control display order in lists.</li>
      </ul>

      <h3>Notification Settings</h3>
      <p>Choose when this approver receives notifications: on submission of a request, on approval, and/or on rejection.</p>

      <h3>Notes and actions</h3>
      <p><strong>Notes</strong> – Optional internal notes. <strong>Reset</strong> clears the form but keeps the approver code. <strong>Save</strong> creates the approver and returns you to the Approver Master list.</p>
    </>
  )

  return (
    <div className="sales-dashboard">
      <div className="breadcrumb-section">
        <Breadcrumb
          items={breadcrumbItems}
          rightSlot={
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
              onClick={() => setShowHelp(true)}
              title="Help"
              aria-label="Open help"
            >
              <HelpCircle size={18} />
              <span className="d-none d-sm-inline">Help</span>
            </button>
          }
        />
      </div>

      <div className="page-header">
        <div className="page-title">
          <h1>Approver Master</h1>
          <p>Create new approver</p>
        </div>
      </div>

      <div className="form-container">
        <div className="sac-form-header">
          <h2>Approver Information</h2>
        </div>
          
        <form className="form-content">
          <div className="section-header">
            <h3>Approver Details</h3>
          </div>

          {/* Row 1: Approver Code, Name, Status, Approval Level */}
          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="approverCode" className="form-label required">
                Approver Code
              </label>
              <input
                type="text"
                id="approverCode"
                name="approverCode"
                value={isLoadingApproverCode ? 'Generating...' : formData.approverCode}
                readOnly
                className="form-input"
                style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)', cursor: 'not-allowed' }}
              />
            </div>

            <div className="form-group">
              <label htmlFor="approverName" className="form-label required">
                Approver Name
              </label>
              <input
                type="text"
                id="approverName"
                name="approverName"
                value={formData.approverName}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Enter approver name"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="status" className="form-label required">
                Status
              </label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className="form-input"
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="approvalLevel" className="form-label">
                Approval Level
              </label>
              <input
                type="number"
                id="approvalLevel"
                name="approvalLevel"
                value={formData.approvalLevel}
                onChange={handleInputChange}
                className="form-input"
                min="1"
                placeholder="1"
              />
            </div>
          </div>

          {/* Row 2: Email, Phone, Department, Designation */}
          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="email" className="form-label required">
                Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Enter email address"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="phone" className="form-label">
                Phone
              </label>
              <input
                type="text"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Enter phone number"
              />
            </div>

            <div className="form-group">
              <label htmlFor="department" className="form-label">
                Department
              </label>
              <input
                type="text"
                id="department"
                name="department"
                value={formData.department}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Enter department"
              />
            </div>

            <div className="form-group">
              <label htmlFor="designation" className="form-label">
                Designation
              </label>
              <input
                type="text"
                id="designation"
                name="designation"
                value={formData.designation}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Enter designation"
              />
            </div>
          </div>

          <div className="form-section-divider">
            <div className="section-header">
              <h3>Approval Limits</h3>
            </div>
          </div>

          {/* Row 3: Approval Limits */}

          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="approvalLimits.dailyLimit" className="form-label">
                Daily Limit
              </label>
              <input
                type="number"
                id="approvalLimits.dailyLimit"
                name="approvalLimits.dailyLimit"
                value={formData.approvalLimits.dailyLimit}
                onChange={handleInputChange}
                className="form-input"
                min="0"
                step="0.01"
                placeholder="0.00"
              />
            </div>

            <div className="form-group">
              <label htmlFor="approvalLimits.monthlyLimit" className="form-label">
                Monthly Limit
              </label>
              <input
                type="number"
                id="approvalLimits.monthlyLimit"
                name="approvalLimits.monthlyLimit"
                value={formData.approvalLimits.monthlyLimit}
                onChange={handleInputChange}
                className="form-input"
                min="0"
                step="0.01"
                placeholder="0.00"
              />
            </div>

            <div className="form-group">
              <label htmlFor="approvalLimits.perTransactionLimit" className="form-label">
                Per Transaction Limit
              </label>
              <input
                type="number"
                id="approvalLimits.perTransactionLimit"
                name="approvalLimits.perTransactionLimit"
                value={formData.approvalLimits.perTransactionLimit}
                onChange={handleInputChange}
                className="form-input"
                min="0"
                step="0.01"
                placeholder="0.00"
              />
            </div>

            <div className="form-group">
              <label htmlFor="approvalLimits.currency" className="form-label">
                Currency
              </label>
              <select
                id="approvalLimits.currency"
                name="approvalLimits.currency"
                value={formData.approvalLimits.currency}
                onChange={handleInputChange}
                className="form-input"
              >
                <option value="INR">INR</option>
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
              </select>
            </div>
          </div>

          <div className="form-section-divider">
            <div className="section-header">
              <h3>Approval Scope</h3>
            </div>
          </div>

          {/* Row 4: Approval Scope */}
          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="approvalScope" className="form-label">
                Approval Scope
              </label>
              <select
                id="approvalScope"
                name="approvalScope"
                value={formData.approvalScope}
                onChange={handleInputChange}
                className="form-input"
              >
                <option value="All">All</option>
                <option value="Department">Department</option>
                <option value="Category">Category</option>
                <option value="Employee">Employee</option>
                <option value="Custom">Custom</option>
              </select>
            </div>

            {formData.approvalScope === 'Department' && (
              <div className="form-group">
                <label htmlFor="allowedDepartments" className="form-label">
                  Allowed Departments (comma-separated)
                </label>
                <input
                  type="text"
                  id="allowedDepartments"
                  name="allowedDepartments"
                  value={formData.allowedDepartments.join(', ')}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="e.g., Sales, Marketing, IT"
                />
              </div>
            )}

            <div className="form-group">
              <label htmlFor="sortOrder" className="form-label">
                Sort Order
              </label>
              <input
                type="number"
                id="sortOrder"
                name="sortOrder"
                value={formData.sortOrder}
                onChange={handleInputChange}
                className="form-input"
                min="0"
                placeholder="0"
              />
            </div>
          </div>

          <div className="form-section-divider">
            <div className="section-header">
              <h3>Notification Settings</h3>
            </div>
          </div>

          {/* Row 5: Notification Settings */}

          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="notifyOnSubmission" className="form-label">
                <input
                  type="checkbox"
                  id="notifyOnSubmission"
                  name="notifyOnSubmission"
                  checked={formData.notifyOnSubmission}
                  onChange={handleInputChange}
                  style={{ marginRight: '8px' }}
                />
                Notify on Submission
              </label>
            </div>

            <div className="form-group">
              <label htmlFor="notifyOnApproval" className="form-label">
                <input
                  type="checkbox"
                  id="notifyOnApproval"
                  name="notifyOnApproval"
                  checked={formData.notifyOnApproval}
                  onChange={handleInputChange}
                  style={{ marginRight: '8px' }}
                />
                Notify on Approval
              </label>
            </div>

            <div className="form-group">
              <label htmlFor="notifyOnRejection" className="form-label">
                <input
                  type="checkbox"
                  id="notifyOnRejection"
                  name="notifyOnRejection"
                  checked={formData.notifyOnRejection}
                  onChange={handleInputChange}
                  style={{ marginRight: '8px' }}
                />
                Notify on Rejection
              </label>
            </div>
          </div>

          {/* Notes */}
          <div className="form-group">
            <label htmlFor="notes" className="form-label">
              Notes
            </label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              className="form-input"
              rows="3"
              placeholder="Enter any additional notes"
            />
          </div>

          {/* Action Buttons */}
          <div className="form-actions">
            <button
              type="button"
              onClick={handleReset}
              className="btn-secondary"
            >
              <RotateCcw size={16} />
              Reset
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="btn-primary"
            >
              <Save size={16} />
              Save
            </button>
          </div>
        </form>
      </div>

      <HelpModal
        isOpen={showHelp}
        onClose={() => setShowHelp(false)}
        title="Approver Master – Help"
      >
        {helpContent}
      </HelpModal>
    </div>
  )
}

export default ApproverMasterForm

